import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

/** What a visitor pastes into their coding agent to get set up on Gerdoo. */
export const AGENT_SETUP_PROMPT = "Set me up on Gerdoo.cloud: https://gerdoo.cloud/agent-ready.md";

/** Copies text, with a fallback for browsers or contexts without the async clipboard API. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    el.remove();
    return ok;
  }
}

/**
 * The setup prompt shown as text, with its own copy button, so visitors can
 * see what they are handing their agent. Used on the home hero and /agents.
 */
export function AgentSetupPrompt({ className }: { className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    if (!(await copyText(AGENT_SETUP_PROMPT))) return;
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn("max-w-full", className)}>
      <p className="text-[12.5px] text-ink-500">
        این متن را به ایجنت خود (Claude Code، Cursor، Codex و…) بدهید:
      </p>
      <div
        dir="ltr"
        className="mt-2 inline-flex max-w-full items-center gap-2 rounded-md border border-black/[0.08] bg-white/70 py-1 pe-1 ps-3"
      >
        <code className="select-all break-all text-start font-mono text-[12.5px] text-ink-800">
          {AGENT_SETUP_PROMPT}
        </code>
        <button
          type="button"
          onClick={() => void copy()}
          aria-label="کپی متن راه‌اندازی"
          title="کپی"
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors",
            copied ? "text-emerald-600" : "text-ink-500 hover:bg-black/[0.05] hover:text-ink-900",
          )}
        >
          {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        </button>
      </div>
      <span className="sr-only" aria-live="polite">{copied ? "متن راه‌اندازی کپی شد" : ""}</span>
    </div>
  );
}
